// saveSceneLook — persist the current viewport look onto the CHARACTER.
//
// The I/O half of sceneLookNodes.js (which is pure and holds the rules). Split
// so the node-building can be unit-tested under `node --test`: this module
// reaches localStorage and the API and cannot be.
//
// It sends the WHOLE blueprint document with only the events graph replaced.
//
// Omitting body and face looks safer and is not. `saveBlueprintToDB` uses the
// omission only for ROUTING (`if (graph === undefined) continue` decides which
// graphs go to their own blueprint-assets row); anything left unrouted falls
// through to the singleton `POST /blueprint`, which REPLACES the stored document
// — so an events-only payload empties body and face. That is the ENG-991 shape
// that bricked eyeline's render chain, and the server refuses it with a 400
// naming the graph, which is what "the server rejected the write" was on the
// first run of this code. Carrying the document through is the fix; `allow_empty`
// is the deliberate-wipe override and a client must never volunteer it.

import { eventGraphState, notifyEventGraphChange } from './eventGraphState.js'
import { buildSceneLookGroup, withSceneLookGroup, findSceneLookGroup } from './sceneLookNodes.js'

/**
 * Write the look into the live graph, the editor's write-through cache, and the
 * server.
 *
 * @param {string} characterId
 * @param {object|null} tune  liveTuneStore state to capture; null CLEARS the
 *   saved look (the character goes back to panel/defaults).
 * @returns {Promise<{ok: boolean, message: string}>} — never throws for a save
 *   failure, but never reports success it did not observe either.
 */
export async function saveSceneLook(characterId, tune) {
  if (!characterId) return { ok: false, message: 'no character loaded' }

  const group = tune ? buildSceneLookGroup(tune) : null
  if (tune && !group) return { ok: false, message: 'nothing to save' }

  // 1. Live graph, so the change takes effect without a reload. The notify is
  //    what makes the open editor redraw and the runtime re-collect starters.
  eventGraphState.nodes = withSceneLookGroup(eventGraphState.nodes, group)
  notifyEventGraphChange()

  // 2. The editor's localStorage cache. Without this the next sync would write
  //    the PRE-edit graph back over what we are about to save — the cache is the
  //    editor's source for a save, not the live ref.
  let cached = false
  try {
    const { getStorageKeysFor } = await import('./loadBlueprint.js')
    const key = getStorageKeysFor(characterId).events
    const raw = localStorage.getItem(key)
    const prev = raw ? JSON.parse(raw) : {}
    localStorage.setItem(key, JSON.stringify({
      ...prev,
      eventNodes: eventGraphState.nodes,
      eventEdges: eventGraphState.edges,
      functions: eventGraphState.functions ?? {},
    }))
    cached = true
  } catch (err) {
    // Loud, and it does NOT abort the server write: the server is the source of
    // truth and a failed cache write only risks a later sync reverting it.
    console.error('[saveSceneLook] could not update the local events cache:', err)
  }

  // 3. The server — the whole document, only `events` replaced (see the header).
  //    The other two graphs come from the server's own copy rather than from
  //    this browser's caches: those can legitimately be absent (a fresh device,
  //    keys scoped under a different user id), and sending an absent graph as an
  //    empty one is the destructive case.
  const { getLatestBlueprint } = await import('./assetManagerApi')
  const { saveBlueprintToDB } = await import('./loadBlueprint.js')
  const { retryTransient } = await import('./retryTransient.js')

  let current
  try {
    // Retried: the AAM 502s this endpoint under load (see retryTransient.js).
    current = await retryTransient(() => getLatestBlueprint(characterId), {
      onRetry: ({ attempt, error }) =>
        console.warn(`[saveSceneLook] read attempt ${attempt} failed (${error?.message ?? error}) — retrying`),
    })
  } catch (err) {
    // 404 = an authoritative "no row yet", so there is nothing to preserve and
    // an events-only document destroys nothing. Anything else is UNKNOWN, and
    // writing on an unknown is how a graph gets emptied — refuse instead.
    //
    // Read the STATUS, not the message. `ApiError` carries the real number
    // (apiClient.js), and the server's 404 body here says "no blueprint found
    // for this character" — which contains no "404" at all, so the substring
    // check this replaces refused a save that was perfectly safe. Note
    // BlueprintEditor's sync path still does the string test; same latent bug,
    // left alone rather than changed underneath a feature it is not part of.
    if (err?.status !== 404) {
      return { ok: false, message: `could not read the current blueprint — ${err?.message ?? err}` }
    }
    current = {}
  }

  const ok = await saveBlueprintToDB(characterId, {
    ...current,
    events: {
      ...(current?.events ?? {}),
      eventNodes: eventGraphState.nodes,
      eventEdges: eventGraphState.edges,
      functions: eventGraphState.functions ?? {},
    },
  })

  if (!ok) {
    // NOT retried here, and the asymmetry with characterLookApi is deliberate.
    // saveBlueprintToDB returns a bare boolean and swallows the status, so this
    // cannot tell a transient 502 (worth asking again) from the ENG-991 guard's
    // 400 (a considered refusal that retrying only spams). Making it retry
    // properly means having that helper surface its error — which the editor's
    // sync path also calls, so it is not a change to make without exercising
    // that path.
    return { ok: false, message: 'could not save to the server — see the console for what it refused' }
  }
  return {
    ok: true,
    message: group
      ? (cached ? 'look saved to this character' : 'saved to the server (local cache write failed)')
      : 'saved look cleared',
  }
}

/** Does this character currently carry a saved look? Reads the LIVE graph. */
export function hasSceneLook() {
  return !!findSceneLookGroup(eventGraphState.nodes)
}
